/*
 * Copyright (c) 2026
 */

export const SNOWFLAKE_TYPE_COUNT = 3
export const SNOWFLAKE_ATLAS_CELL = 512
/** Extra cell: the penis crystal with no symbol, so an erect shaft can replace it. */
export const SNOWFLAKE_ATLAS_COLUMNS = SNOWFLAKE_TYPE_COUNT + 1
export const PENIS_CRYSTAL_CELL = SNOWFLAKE_TYPE_COUNT

const CENTER = SNOWFLAKE_ATLAS_CELL / 2
const ARM_COUNT = 6
const ARM_LENGTH = 214
const BRANCH_ANGLE = Math.PI / 3
/** Feathered crystal stays in the outer half of each arm. */
const CRYSTAL_RADIUS = 156
const SYMBOL_SCALE = 2.6
const OUTLINE_WIDTH = 14

/** Maps a symbol-space distance into quad-local units (−1..1 across the cell). */
function atlasLocal(userUnits: number): number {
  return (userUnits * SYMBOL_SCALE) / CENTER
}

/** Penis layout in quad-local units. +y points from the glans toward the balls. */
export const PENIS_SHAPE = {
  rootY: atlasLocal(40),
  shaftLength: atlasLocal(62),
  shaftHalfWidth: atlasLocal(12),
  glansOffset: atlasLocal(14),
  glansRx: atlasLocal(24),
  glansRy: atlasLocal(28),
  ballX: atlasLocal(24),
  ballY: atlasLocal(36),
  ballRadius: atlasLocal(22),
  /** Visible outline thickness. The interior cut keeps only the outer half of the stroke. */
  outline: atlasLocal(OUTLINE_WIDTH / 2),
} as const
const DETAIL_WIDTH = 5.5

type MotifPainter = (ctx: CanvasRenderingContext2D) => void

function traceCircle(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number) {
  ctx.beginPath()
  ctx.arc(x, y, radius, 0, Math.PI * 2)
}

function fillCircle(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number) {
  traceCircle(ctx, x, y, radius)
  ctx.fill()
}

function traceRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  const curve = Math.min(radius, width / 2, height / 2)
  ctx.beginPath()
  ctx.moveTo(x + curve, y)
  ctx.arcTo(x + width, y, x + width, y + height, curve)
  ctx.arcTo(x + width, y + height, x, y + height, curve)
  ctx.arcTo(x, y + height, x, y, curve)
  ctx.arcTo(x, y, x + width, y, curve)
  ctx.closePath()
}

function fillRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  traceRoundedRect(ctx, x, y, width, height, radius)
  ctx.fill()
}

function traceAlmond(ctx: CanvasRenderingContext2D, halfWidth: number, top: number, bottom: number) {
  const height = bottom - top
  ctx.beginPath()
  ctx.moveTo(0, top)
  ctx.bezierCurveTo(halfWidth, top + height * 0.28, halfWidth, top + height * 0.62, 0, bottom)
  ctx.bezierCurveTo(-halfWidth, top + height * 0.62, -halfWidth, top + height * 0.28, 0, top)
  ctx.closePath()
}

function drawBarbs(ctx: CanvasRenderingContext2D, length: number, width: number) {
  const count = length > 64 ? 3 : 2
  ctx.lineWidth = Math.max(2.2, width * 0.42)
  for (let barb = 1; barb <= count; barb++) {
    const t = barb / (count + 1)
    const origin = length * (0.3 + t * 0.5)
    const reach = length * 0.2 * (1 - t * 0.4)
    ctx.beginPath()
    ctx.moveTo(origin, 0)
    ctx.lineTo(origin + reach * 0.6, reach * 0.5)
    ctx.moveTo(origin, 0)
    ctx.lineTo(origin + reach * 0.6, -reach * 0.5)
    ctx.stroke()
  }
}

function drawSideBranch(ctx: CanvasRenderingContext2D, length: number) {
  const width = Math.max(3.6, length * 0.1)
  ctx.lineWidth = width
  ctx.beginPath()
  ctx.moveTo(0, 0)
  ctx.lineTo(length, 0)
  ctx.stroke()
  ctx.lineWidth = Math.max(1.5, width * 0.34)
  ctx.beginPath()
  ctx.moveTo(1, 0)
  ctx.lineTo(length - 1, 0)
  ctx.stroke()
  drawBarbs(ctx, length, width)
  ctx.beginPath()
  ctx.moveTo(length + 7, 0)
  ctx.lineTo(length - 1, 3.4)
  ctx.lineTo(length - 1, -3.4)
  ctx.closePath()
  ctx.fill()
}

/** One stellar-dendrite arm, drawn along +x. Side branches grow at 60°. */
function drawArm(ctx: CanvasRenderingContext2D, symbolEdge: number) {
  const lineStart = Math.min(symbolEdge, ARM_LENGTH - 12)
  const crystalStart = Math.max(lineStart, CRYSTAL_RADIUS)

  ctx.beginPath()
  ctx.moveTo(crystalStart, -7)
  ctx.lineTo(ARM_LENGTH - 6, -2.5)
  ctx.lineTo(ARM_LENGTH + 12, 0)
  ctx.lineTo(ARM_LENGTH - 6, 2.5)
  ctx.lineTo(crystalStart, 7)
  ctx.closePath()
  ctx.save()
  ctx.globalAlpha = 0.38
  ctx.fill()
  ctx.restore()

  ctx.lineWidth = 11
  ctx.beginPath()
  ctx.moveTo(lineStart, 0)
  ctx.lineTo(ARM_LENGTH, 0)
  ctx.stroke()
  ctx.lineWidth = 3
  ctx.beginPath()
  ctx.moveTo(lineStart, 0)
  ctx.lineTo(ARM_LENGTH - 6, 0)
  ctx.stroke()

  const stations = 6
  const firstOuterStation = stations / 2
  for (let station = firstOuterStation; station < stations; station++) {
    const t = 0.2 + (station / (stations - 1)) * 0.72
    const x = 40 + t * (ARM_LENGTH - 56)
    if (x < crystalStart) {
      continue
    }
    const envelope = 0.42 + Math.sin(Math.PI * t) * 0.72
    const length = envelope * ARM_LENGTH * 0.4 * (1 - t * 0.22)
    for (const side of [1, -1]) {
      ctx.save()
      ctx.translate(x, 0)
      ctx.rotate(side * BRANCH_ANGLE)
      drawSideBranch(ctx, length)
      ctx.restore()
    }
  }

  ctx.beginPath()
  ctx.moveTo(ARM_LENGTH + 18, 0)
  ctx.lineTo(ARM_LENGTH - 2, 6)
  ctx.lineTo(ARM_LENGTH - 2, -6)
  ctx.closePath()
  ctx.fill()
}

/** Radius where each arm's centerline meets the symbol outline. */
function symbolEdges(layer: HTMLCanvasElement): number[] {
  const edges = Array.from({ length: ARM_COUNT }, () => CRYSTAL_RADIUS)
  const layerCtx = layer.getContext('2d', { willReadFrequently: true })
  if (!layerCtx) {
    return edges
  }
  const { data, width, height } = layerCtx.getImageData(0, 0, layer.width, layer.height)
  const alphaAt = (x: number, y: number) => {
    if (x < 0 || y < 0 || x >= width || y >= height) {
      return 0
    }
    return data[(y * width + x) * 4 + 3] ?? 0
  }
  for (let arm = 0; arm < ARM_COUNT; arm++) {
    const angle = -Math.PI / 2 + (arm * Math.PI * 2) / ARM_COUNT
    const hx = Math.cos(angle)
    const hy = Math.sin(angle)
    let edge = 0
    for (let radius = 0; radius <= ARM_LENGTH + 24; radius++) {
      const x = Math.round(CENTER + hx * radius)
      const y = Math.round(CENTER + hy * radius)
      if (alphaAt(x, y) >= 24) {
        edge = radius
      }
    }
    if (edge > 0) {
      edges[arm] = edge
    }
  }
  return edges
}

/** Crystalline six-arm base, with one arm pointing up. */
function drawSnowflakeBase(ctx: CanvasRenderingContext2D, edges: readonly number[]) {
  for (let arm = 0; arm < ARM_COUNT; arm++) {
    ctx.save()
    ctx.rotate(-Math.PI / 2 + (arm * Math.PI * 2) / ARM_COUNT)
    drawArm(ctx, edges[arm] ?? CRYSTAL_RADIUS)
    ctx.restore()
  }
}

/** Erases the filled interior so only the outer half of the stroke remains. */
function cutInterior(ctx: CanvasRenderingContext2D, draw: (ctx: CanvasRenderingContext2D) => void) {
  ctx.save()
  ctx.shadowBlur = 0
  ctx.globalCompositeOperation = 'destination-out'
  draw(ctx)
  ctx.restore()
}

/**
 * Open outline of one breast: elliptical outer curve, full bottom, and a cleavage peak.
 * Both sides pass through the same bottom point so the pair meets.
 */
function traceBreast(ctx: CanvasRenderingContext2D, side: number) {
  const s = side
  ctx.beginPath()
  ctx.moveTo(s * 64, -38)
  ctx.bezierCurveTo(s * 82, -16, s * 92, 10, s * 78, 30)
  ctx.bezierCurveTo(s * 64, 50, s * 34, 52, s * 16, 42)
  ctx.bezierCurveTo(s * 4, 34, 0, 22, 0, 14)
  ctx.bezierCurveTo(s * 2, -4, s * 10, -24, s * 16, -38)
}

/** Areola ring and a smaller nipple ring, sitting in the lower half of the breast. */
function drawAreola(ctx: CanvasRenderingContext2D, side: number) {
  ctx.save()
  ctx.translate(side * 44, 16)
  ctx.lineWidth = 3.4
  traceCircle(ctx, 0, 0, 8.5)
  ctx.stroke()
  traceCircle(ctx, 0, 0, 3.1)
  ctx.stroke()
  ctx.restore()
}

/** Pair of open breast outlines that meet at the cleavage, each with an areola ring. */
function drawBoobs(ctx: CanvasRenderingContext2D) {
  ctx.lineWidth = OUTLINE_WIDTH / 2
  for (const side of [-1, 1]) {
    traceBreast(ctx, side)
    ctx.stroke()
  }
  for (const side of [-1, 1]) {
    drawAreola(ctx, side)
  }
}

/** Outline of the glans, shaft, and testes, with the ridge and cleft stroked inside. */
function drawPenis(ctx: CanvasRenderingContext2D) {
  traceCircle(ctx, -24, 36, 22)
  ctx.stroke()
  traceCircle(ctx, 24, 36, 22)
  ctx.stroke()
  traceRoundedRect(ctx, -12, -22, 24, 62, 10)
  ctx.stroke()
  ctx.beginPath()
  ctx.ellipse(0, -36, 24, 28, 0, 0, Math.PI * 2)
  ctx.stroke()
  cutInterior(ctx, (cut) => {
    fillCircle(cut, -24, 36, 22)
    fillCircle(cut, 24, 36, 22)
    fillRoundedRect(cut, -12, -22, 24, 62, 10)
    cut.beginPath()
    cut.ellipse(0, -36, 24, 28, 0, 0, Math.PI * 2)
    cut.fill()
  })
  ctx.lineWidth = DETAIL_WIDTH
  ctx.beginPath()
  ctx.moveTo(0, -56)
  ctx.lineTo(0, -44)
  ctx.stroke()
  ctx.beginPath()
  ctx.ellipse(0, -16, 16, 7, 0, Math.PI * 0.12, Math.PI * 0.88)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(0, 26)
  ctx.lineTo(0, 52)
  ctx.stroke()
}

/** Almond outline with an inner opening, a small ring, and two inner strokes. */
function drawVulva(ctx: CanvasRenderingContext2D) {
  traceAlmond(ctx, 46, -68, 70)
  ctx.stroke()
  cutInterior(ctx, (cut) => {
    traceAlmond(cut, 46, -68, 70)
    cut.fill()
  })
  ctx.lineWidth = DETAIL_WIDTH
  traceAlmond(ctx, 18, -30, 46)
  ctx.stroke()
  traceCircle(ctx, 0, -34, 6.5)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(-7, -12)
  ctx.quadraticCurveTo(-9, 10, -2, 28)
  ctx.moveTo(7, -12)
  ctx.quadraticCurveTo(9, 10, 2, 28)
  ctx.stroke()
}

const MOTIFS: readonly MotifPainter[] = [drawBoobs, drawPenis, drawVulva]

function paintCell(ctx: CanvasRenderingContext2D, index: number, paintMotif: MotifPainter) {
  const layer = renderSymbolLayer(paintMotif)
  ctx.save()
  ctx.translate(index * SNOWFLAKE_ATLAS_CELL + CENTER, CENTER)
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.strokeStyle = '#ffffff'
  ctx.fillStyle = '#ffffff'
  ctx.shadowBlur = 0
  drawSnowflakeBase(ctx, layer ? symbolEdges(layer) : [])
  if (layer) {
    paintSymbol(ctx, layer)
  }
  ctx.restore()
}

/** Strokes one symbol into its own canvas so the interior cut cannot erase the crystal. */
function renderSymbolLayer(paintMotif: MotifPainter): HTMLCanvasElement | null {
  const layer = document.createElement('canvas')
  layer.width = SNOWFLAKE_ATLAS_CELL
  layer.height = SNOWFLAKE_ATLAS_CELL
  const layerCtx = layer.getContext('2d', { willReadFrequently: true })
  if (!layerCtx) {
    return null
  }
  layerCtx.translate(CENTER, CENTER)
  layerCtx.scale(SYMBOL_SCALE, SYMBOL_SCALE)
  layerCtx.lineCap = 'round'
  layerCtx.lineJoin = 'round'
  layerCtx.strokeStyle = '#ffffff'
  layerCtx.fillStyle = '#ffffff'
  layerCtx.lineWidth = OUTLINE_WIDTH
  paintMotif(layerCtx)
  return layer
}

/**
 * Composites a symbol layer over the crystal.
 * The interior cut stays on that layer, so the crystal shows through.
 */
function paintSymbol(ctx: CanvasRenderingContext2D, layer: HTMLCanvasElement) {
  ctx.save()
  ctx.shadowColor = 'rgba(255,255,255,0.85)'
  ctx.shadowBlur = 10
  ctx.drawImage(layer, -CENTER, -CENTER)
  ctx.restore()
}

/** Crystal only, using the penis outline so the arms still stop at the symbol. */
function paintCrystalCell(ctx: CanvasRenderingContext2D, index: number, edges: readonly number[]) {
  ctx.save()
  ctx.translate(index * SNOWFLAKE_ATLAS_CELL + CENTER, CENTER)
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.strokeStyle = '#ffffff'
  ctx.fillStyle = '#ffffff'
  ctx.shadowBlur = 0
  drawSnowflakeBase(ctx, edges)
  ctx.restore()
}

/** Rasterizes the three snowflakes, plus a bare penis crystal, into one horizontal atlas. */
export function createSnowflakeAtlas(): HTMLCanvasElement | null {
  const canvas = document.createElement('canvas')
  canvas.width = SNOWFLAKE_ATLAS_CELL * SNOWFLAKE_ATLAS_COLUMNS
  canvas.height = SNOWFLAKE_ATLAS_CELL
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return null
  }
  for (const [index, paintMotif] of MOTIFS.entries()) {
    paintCell(ctx, index, paintMotif)
  }
  const penisLayer = renderSymbolLayer(drawPenis)
  paintCrystalCell(ctx, PENIS_CRYSTAL_CELL, penisLayer ? symbolEdges(penisLayer) : [])
  return canvas
}
